import type { TimeSlot, TimeRange } from '../hooks/useSchedulerTimeSlots'
import type { CalendarGame } from '../components/CalendarGameCard'
import type { SchedulerConstraint } from '../hooks/useSchedulerConstraints'

export interface HourSlot {
  start: string // HH:mm
  end: string // HH:mm
}

/**
 * Return sorted, deduplicated TimeRange[] for a given facility + day-of-week.
 * Optionally filters by divisionId — if a time slot has a `divisions` array,
 * only include it when the game's division is listed (or when the array is empty/absent,
 * meaning the slot is open to all divisions).
 */
export function getTimeRangesForFacilityAndDay(
  timeSlots: TimeSlot[],
  facilityId: string,
  dayOfWeek: number,
  divisionId?: string
): TimeRange[] {
  const seen = new Set<string>()
  const ranges: TimeRange[] = []
  for (const ts of timeSlots) {
    if (!ts.is_active || ts.facility_id !== facilityId) {
      continue
    }
    if (!ts.days.includes(dayOfWeek)) {
      continue
    }
    // If the slot is restricted to certain divisions, skip unless the game's division matches
    if (
      divisionId &&
      ts.divisions &&
      ts.divisions.length > 0 &&
      !ts.divisions.includes(divisionId)
    ) {
      continue
    }
    for (const tr of ts.time_ranges) {
      const key = `${tr.start_time}-${tr.end_time}`
      if (!seen.has(key)) {
        seen.add(key)
        ranges.push(tr)
      }
    }
  }
  ranges.sort((a, b) => a.start_time.localeCompare(b.start_time))
  return ranges
}

/**
 * Return sorted, deduplicated TimeRange[] from schedule-level time slots
 * (facility_id is null) for a given day-of-week.
 * The API validates against these when only starts_at/ends_at are sent
 * (e.g. drag-and-drop rescheduling without changing facility).
 */
export function getScheduleLevelTimeRangesForDay(
  timeSlots: TimeSlot[],
  dayOfWeek: number,
  divisionId?: string
): TimeRange[] {
  const seen = new Set<string>()
  const ranges: TimeRange[] = []
  for (const ts of timeSlots) {
    if (!ts.is_active) {
      continue
    }
    // Only include schedule-level slots (no specific facility)
    if (ts.facility_id !== null && ts.facility_id !== undefined) {
      continue
    }
    if (!ts.days.includes(dayOfWeek)) {
      continue
    }
    if (
      divisionId &&
      ts.divisions &&
      ts.divisions.length > 0 &&
      !ts.divisions.includes(divisionId)
    ) {
      continue
    }
    for (const tr of ts.time_ranges) {
      const key = `${tr.start_time}-${tr.end_time}`
      if (!seen.has(key)) {
        seen.add(key)
        ranges.push(tr)
      }
    }
  }
  ranges.sort((a, b) => a.start_time.localeCompare(b.start_time))
  return ranges
}

/**
 * Split time ranges into 1-hour slots.
 * E.g. 09:00-17:00 → [09:00-10:00, 10:00-11:00, ..., 16:00-17:00]
 */
export function splitIntoHourSlots(ranges: TimeRange[]): HourSlot[] {
  const slots: HourSlot[] = []
  const seen = new Set<string>()
  for (const r of ranges) {
    const [startH, startM] = r.start_time.slice(0, 5).split(':').map(Number)
    const [endH, endM] = r.end_time.slice(0, 5).split(':').map(Number)
    const startMinutes = startH * 60 + startM
    const endMinutes = endH * 60 + endM
    for (let m = startMinutes; m + 60 <= endMinutes; m += 60) {
      const slotStart = `${String(Math.floor(m / 60)).padStart(
        2,
        '0'
      )}:${String(m % 60).padStart(2, '0')}`
      const slotEnd = `${String(Math.floor((m + 60) / 60)).padStart(
        2,
        '0'
      )}:${String((m + 60) % 60).padStart(2, '0')}`
      const key = `${slotStart}-${slotEnd}`
      if (!seen.has(key)) {
        seen.add(key)
        slots.push({ start: slotStart, end: slotEnd })
      }
    }
  }
  slots.sort((a, b) => a.start.localeCompare(b.start))
  return slots
}

/**
 * Build a diff object comparing current edit form values to the original game.
 * Returns only changed fields mapped to API format. Returns null if nothing changed.
 */
export function buildEditDiff(
  editForm: {
    venueId: string
    facilityId: string
    homeTeamId: string
    awayTeamId: string
    startsAt: string
    endsAt: string
  },
  original: {
    locationId?: string
    surfaceId?: string
    homeTeamId?: string
    awayTeamId?: string
    startsAt?: string
    endsAt?: string
  }
): Record<string, unknown> | null {
  const data: Record<string, unknown> = {}

  if (editForm.venueId && editForm.venueId !== original.locationId) {
    data.venue_id = editForm.venueId
  }
  if (editForm.facilityId && editForm.facilityId !== original.surfaceId) {
    data.facility_id = editForm.facilityId
  }
  if (editForm.homeTeamId && editForm.homeTeamId !== original.homeTeamId) {
    data.home_team_id = editForm.homeTeamId
  }
  if (editForm.awayTeamId && editForm.awayTeamId !== original.awayTeamId) {
    data.visiting_team_id = editForm.awayTeamId
  }
  if (editForm.startsAt && editForm.startsAt !== original.startsAt) {
    data.starts_at = editForm.startsAt
  }
  if (editForm.endsAt && editForm.endsAt !== original.endsAt) {
    data.ends_at = editForm.endsAt
  }

  return Object.keys(data).length > 0 ? data : null
}

/**
 * Format a Date as YYYY-MM-DD using local time (avoids UTC shift from toISOString).
 */
export function formatLocalDate(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/**
 * Compute dates that should be blocked (not shown as feasible green)
 * based on hard, enabled scheduler constraints.
 *
 * Handles: min_rest_days, max_games_per_team_per_day, team_bye_period.
 * Returns a Set of YYYY-MM-DD strings.
 */
export function computeConstraintBlockedDates(
  activeGame: CalendarGame,
  allGames: CalendarGame[],
  constraints: SchedulerConstraint[]
): Set<string> {
  const blocked = new Set<string>()
  const teamIds = [activeGame.homeTeamId, activeGame.awayTeamId].filter(Boolean)
  if (teamIds.length === 0) return blocked

  // Build team → date → game count index (excluding the dragged game)
  const teamDateCounts = new Map<string, Map<string, number>>()
  for (const game of allGames) {
    if (game.id === activeGame.id) continue
    const gameDate = formatLocalDate(
      game.startsAt ? new Date(game.startsAt) : new Date(game.date)
    )
    for (const tid of [game.homeTeamId, game.awayTeamId]) {
      if (!tid) continue
      let dateCounts = teamDateCounts.get(tid)
      if (!dateCounts) {
        dateCounts = new Map()
        teamDateCounts.set(tid, dateCounts)
      }
      dateCounts.set(gameDate, (dateCounts.get(gameDate) ?? 0) + 1)
    }
  }

  // Helper: check if a constraint applies to the dragged game's division/teams
  const constraintApplies = (c: SchedulerConstraint): boolean => {
    if (c.division_uids && c.division_uids.length > 0) {
      if (!c.division_uids.includes(activeGame.divisionId)) return false
    }
    return true
  }

  const constraintAppliesToTeam = (
    c: SchedulerConstraint,
    teamId: string
  ): boolean => {
    if (c.team_uids && c.team_uids.length > 0) {
      if (!c.team_uids.includes(teamId)) return false
    }
    return true
  }

  for (const c of constraints) {
    if (!c.is_hard || !c.is_enabled) continue
    if (!constraintApplies(c)) continue

    const params = c.parameters as Record<string, unknown>

    if (c.type.code === 'min_rest_days') {
      const minDays = Number(params.min_days ?? params.days ?? 0)
      if (minDays <= 0) continue

      for (const tid of teamIds) {
        if (!constraintAppliesToTeam(c, tid)) continue
        const dateCounts = teamDateCounts.get(tid)
        if (!dateCounts) continue

        dateCounts.forEach((_count, dateStr) => {
          const base = new Date(dateStr + 'T00:00:00')
          for (let offset = 1; offset <= minDays; offset++) {
            const before = new Date(base)
            before.setDate(before.getDate() - offset)
            blocked.add(formatLocalDate(before))

            const after = new Date(base)
            after.setDate(after.getDate() + offset)
            blocked.add(formatLocalDate(after))
          }
        })
      }
    } else if (c.type.code === 'max_games_per_team_per_day') {
      const maxGames = Number(params.max_games ?? params.max ?? 1)

      for (const tid of teamIds) {
        if (!constraintAppliesToTeam(c, tid)) continue
        const dateCounts = teamDateCounts.get(tid)
        if (!dateCounts) continue

        dateCounts.forEach((count, dateStr) => {
          if (count >= maxGames) {
            blocked.add(dateStr)
          }
        })
      }
    } else if (c.type.code === 'team_bye_period') {
      const startDate = params.start_date as string | undefined
      const endDate = params.end_date as string | undefined
      if (!startDate || !endDate) continue

      // Check if any team in the dragged game is scoped
      const hasTeamScope = teamIds.some((tid) =>
        constraintAppliesToTeam(c, tid)
      )
      if (!hasTeamScope) continue

      const cur = new Date(startDate + 'T00:00:00')
      const end = new Date(endDate + 'T00:00:00')
      while (cur <= end) {
        blocked.add(formatLocalDate(cur))
        cur.setDate(cur.getDate() + 1)
      }
    }
  }

  return blocked
}
